import React, { useEffect, useMemo, useState } from "react";
import { saveAffFromURL } from "../utils/aff";

/* ===== セクション種別（構造は維持） ===== */
type Section =
  | { kind: "image"; src: string; id?: string }
  | { kind: "banner"; id: "line-banner-1" | "line-banner-2" };

/* ===== 定数 ===== */
const base = "/assets/member/";

/* ENV優先。無ければ /api/auth/line */
const LINE_URL: string =
  (process.env as any)?.REACT_APP_LINE_REDIRECT_URL || "/api/auth/line";

/* ★ バナー上下の詰め量（マイナスで詰める） */
const BANNER_PULL = -8;

/* ===== 並び順（ジェイ指定） ===== */
const SECTIONS: Section[] = [
  { kind: "image", src: `${base}LP-top1.webp`, id: "lp-top1" },

  // ★変更：LP-top2.webp → home1-koukai.webp
  { kind: "image", src: `${base}home1-koukai.webp`, id: "lp-top2" },

  { kind: "banner", id: "line-banner-1" },

  { kind: "image", src: `${base}LP-top3.webp`, id: "lp-top3" },

  // ★変更：LP-top4.webp → home2-jisseki.webp
  { kind: "image", src: `${base}home2-jisseki.webp`, id: "lp-top4" },

  { kind: "image", src: `${base}LP-top5.webp`, id: "lp-top5" },
  { kind: "image", src: `${base}LP-top6.webp`, id: "lp-top6" },

  { kind: "banner", id: "line-banner-2" },

  { kind: "image", src: `${base}LP-top7.webp`, id: "lp-top7" },
  { kind: "image", src: `${base}LP-top8.webp`, id: "lp-top8" },

  { kind: "image", src: `${base}LP-top9.webp`, id: "lp-top9" },
];

/* ===== ユーティリティ ===== */
const fmtMoney = (n: any) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "";
  return v.toLocaleString("ja-JP");
};

const pickText = (obj: any, keys: string[]) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
};

const fmtDateJP = (raw: any) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.includes("月") && s.includes("日")) return s;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  const wd = ["日", "月", "火", "水", "木", "金", "土"][d.getDay()];
  return `${d.getMonth() + 1}月${d.getDate()}日(${wd})`;
};

// 「item（例：3連単 1-4-2）」から券種と買い目を最優先で抜く
const parseItemTicketCombo = (item: string) => {
  const s = String(item || "").replace(/\s+/g, " ").trim();
  if (!s) return { ticket: "", combo: "" };

  // combo: 1-4-2 / 1-2-3-4 なども一応拾う（まず3桁優先）
  const combo =
    (s.match(/(\d\s*[-/]\s*\d\s*[-/]\s*\d)/)?.[1] || "")
      .replace(/\s*/g, "")
      .replace(/\//g, "-");

  // ticket: よくある券種ワード
  const ticket =
    s.match(/(3連単|3連複|2連単|2連複|単勝|複勝)/)?.[1] || "";

  return { ticket, combo };
};

const extractCombo = (obj: any) => {
  // ① item があるなら最優先
  const item = pickText(obj, ["item", "buyItem", "betItem", "ticketItem", "wager", "summary", "detail"]);
  if (item) {
    const { combo } = parseItemTicketCombo(item);
    if (combo) return combo;
  }

  // ② 専用キー
  const direct = pickText(obj, [
    "combo",
    "buy",
    "hitCombo",
    "resultCombo",
    "kumiai",
    "kumiawase",
    "numbers",
    "pattern",
    "bet",
    "kaime",
    "selection",
  ]);
  if (direct) {
    const m = String(direct).match(/(\d\s*[-/]\s*\d\s*[-/]\s*\d)/);
    if (m?.[1]) return m[1].replace(/\s*/g, "").replace(/\//g, "-");
    return String(direct).trim();
  }

  // ③ テキスト系から「1-4-2」だけ抜く
  const text = pickText(obj, ["resultText", "display", "hitText", "text", "label", "note"]);
  if (text) {
    const m = text.match(/(\d\s*[-/]\s*\d\s*[-/]\s*\d)/);
    if (m?.[1]) return m[1].replace(/\s*/g, "").replace(/\//g, "-");
  }

  // ④ ネスト
  const nested = pickText(obj?.hit, ["combo", "numbers", "pattern", "buy", "bet"]);
  if (nested) return nested;
  const nested2 = pickText(obj?.result, ["combo", "numbers", "pattern", "buy", "bet"]);
  if (nested2) return nested2;

  return "";
};

const extractTicket = (obj: any) => {
  // ① item があるなら最優先
  const item = pickText(obj, ["item", "buyItem", "betItem", "ticketItem", "wager", "summary", "detail"]);
  if (item) {
    const { ticket } = parseItemTicketCombo(item);
    if (ticket) return ticket;
  }

  // ② 直接キー
  const t = pickText(obj, [
    "ticketLabel",
    "betType",
    "type",
    "ticket",
    "kind",
    "ticketType",
    "wagerType",
    "betKind",
    "ticketKind",
    "kindName",
  ]);
  if (t) return t;

  // ③ テキストから拾う
  const text = pickText(obj, ["resultText", "display", "summary", "hitText", "text"]);
  if (text) {
    const m = text.match(/(3連単|3連複|2連単|2連複|単勝|複勝)/);
    if (m?.[1]) return m[1];
  }
  return "";
};

/* ===== ページ ===== */
export default function EntryLP() {
  const [recommended, setRecommended] = useState<any[]>([]);
  const [recentResults, setRecentResults] = useState<any[]>([]);

  useEffect(() => {
    // ① URL の bom / aff を localStorage に保存
    saveAffFromURL();

    // ② bom をサーバー(session/cookie)にも保存
    const params = new URLSearchParams(window.location.search);
    const bom = params.get("bom");
    if (bom) {
      fetch("/api/public/prefix-aff", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ bom }),
      }).catch(() => {});
    }

    // ③ 本日の公開情報（おすすめ：最大3）
    fetch("/api/public/recommended", { method: "GET", credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        const arr = Array.isArray(data) ? data : data?.items || data?.plans || [];
        setRecommended(Array.isArray(arr) ? arr.slice(0, 3) : []);
      })
      .catch(() => setRecommended([]));

    // ④ 直近の的中実績（直近3件）
    fetch("/api/public/results", { method: "GET", credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        const arr = Array.isArray(data) ? data : data?.items || data?.rows || [];
        const sorted = Array.isArray(arr)
          ? [...arr].sort((a, b) => {
              const da = new Date(
                a?.date || a?.startAt || a?.createdAt || a?.publishedAt || a?.purchasedAt || 0
              ).getTime();
              const db = new Date(
                b?.date || b?.startAt || b?.createdAt || b?.publishedAt || b?.purchasedAt || 0
              ).getTime();
              return db - da;
            })
          : [];
        setRecentResults(sorted.slice(0, 3));
      })
      .catch(() => setRecentResults([]));
  }, []);

  // LINEログイン遷移（affCode=bom付与）
  const handleLogin = (
    e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => {
    if (e) e.preventDefault();

    const code = localStorage.getItem("affCode");
    let url = LINE_URL;

    if (code) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}bom=${encodeURIComponent(code)}`;
    }

    window.location.href = url;
  };

  // ===== 公開情報：最大3件／1行固定／…無し（LP.tsxのサイズに合わせる）
  const recoLines = useMemo(() => {
    return (recommended || []).slice(0, 3).map((p: any) => {
      const title = pickText(p, ["name", "title", "planName", "displayName", "label"]);
      const off = pickText(p, ["discountLabel", "campaignLabel", "tag"]);
      if (off && title) return `<<${off}>>${title}`;
      return title || "";
    });
  }, [recommended]);

  // ===== 実績：3件（LP.tsxと同じ「日付/プラン」「券種+買い目」「金額 的中」）
  const resultRows = useMemo(() => {
    return (recentResults || []).slice(0, 3).map((r: any) => {
      const dateRaw = pickText(r, ["dateLabel", "date", "startAt", "day", "ymd", "displayDate", "createdAt"]);
      const item = pickText(r, ["item", "buyItem", "betItem", "ticketItem", "wager", "summary", "detail"]);

      // itemがあれば最優先で解析
      const parsed = item ? parseItemTicketCombo(item) : { ticket: "", combo: "" };

      const ticket = parsed.ticket || extractTicket(r) || "3連単";
      const combo = parsed.combo || extractCombo(r) || ""; // ← ここが空だと買い目が出ない

      const plan = pickText(r, ["planName", "planLabel", "plan", "title", "name"]);
      const payout = r?.amount ?? r?.payout ?? r?.pay ?? r?.winAmount ?? r?.returnAmount ?? r?.money;
      const status = pickText(r, ["statusLabel", "status", "hit", "result"]) || "的中";

      return {
        date: fmtDateJP(dateRaw),
        ticket,
        combo,
        plan,
        payout,
        status,
      };
    });
  }, [recentResults]);

  return (
    <div className="lp" style={{ margin: 0, padding: 0, background: "#fff" }}>
      <style>{`
        .lp, .lp * { box-sizing: border-box; }
        img { vertical-align: top; }

        /* バナーのふわふわ（既存維持） */
        @keyframes floatLuxury {
          0%   { transform: translate3d(0,0,0) scale(1); }
          50%  { transform: translate3d(0,-6px,0) scale(1.01); }
          100% { transform: translate3d(0,0,0) scale(1); }
        }
        .lineButtonLuxury {
          animation: floatLuxury 3.2s cubic-bezier(.2,.8,.2,1) infinite;
          will-change: transform;
          transform: translateZ(0);
          filter: drop-shadow(0 10px 18px rgba(0,0,0,0.12));
        }

        /* =========================
           LP.tsx 同等：公開情報おすすめ（点滅＋下線flow）
           ========================= */
        .koukaiRecoInImage {
          position: absolute;
          left: 26px;
          right: 26px;
          top: 74px;
          z-index: 4;

          display: flex;
          flex-direction: column;
          gap: 10px;
          box-sizing: border-box;

          max-height: 120px;
          overflow: hidden;

          pointer-events: none;
        }
        @media (min-width: 768px) {
          .koukaiRecoInImage{
            left: 5%;
            right: 5%;
            top: 24%;
            max-height: 34%;
            gap: 12px;
          }
        }
        @media (min-width: 1100px) {
          .koukaiRecoInImage{ top: 26%; }
        }

        @keyframes recoBreath {
          0%   { color: #1b2a33; }
          50%  { color: #b79b4a; }
          100% { color: #1b2a33; }
        }
        @keyframes recoUnderlineFlow {
          0%   { background-position: 0% 100%; }
          100% { background-position: 200% 100%; }
        }
        .koukaiRecoText {
          position: relative;
          font-weight: 900;
          font-size: clamp(13px, 4.2vw, 16px); /* ★LPと同一 */
          line-height: 1.22;
          letter-spacing: .01em;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: clip;

          padding-bottom: 8px;

          text-shadow:
            0 1px 2px rgba(255,255,255,.75),
            0 2px 10px rgba(0,0,0,.12);

          animation: recoBreath 2.8s ease-in-out infinite;
        }
        .koukaiRecoText::after {
          content: "";
          position: absolute;
          left: 0;
          width: min(92%, 520px);
          bottom: 2px;
          height: 3px;
          border-radius: 999px;

          background-image: linear-gradient(
            90deg,
            rgba(183,155,74,0) 0%,
            rgba(183,155,74,.55) 18%,
            rgba(183,155,74,1) 50%,
            rgba(183,155,74,.55) 82%,
            rgba(183,155,74,0) 100%
          );
          background-size: 200% 100%;
          background-position: 0% 100%;
          animation: recoUnderlineFlow 2.7s linear infinite;

          opacity: .98;
          filter:
            drop-shadow(0 1px 2px rgba(255,255,255,.65))
            drop-shadow(0 2px 8px rgba(0,0,0,.12));
        }
        .koukaiRecoText.d1 { animation-delay: 0s; }
        .koukaiRecoText.d2 { animation-delay: .2s; }
        .koukaiRecoText.d3 { animation-delay: .4s; }
        .koukaiRecoText.d1::after { animation-delay: 0s; }
        .koukaiRecoText.d2::after { animation-delay: .18s; }
        .koukaiRecoText.d3::after { animation-delay: .36s; }

        /* =========================
           LP.tsx 同等：直近的中実績（枠＋金額点滅）
           ========================= */
        .hitsInCard {
          position: absolute;
          left: 5%;
          right: 5%;
          top: 22%;
          z-index: 4;
          box-sizing: border-box;

          display: flex;
          flex-direction: column;
          gap: clamp(10px, 1.6vw, 14px);

          pointer-events: none;
        }

        .hitBlock {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 6px;

          padding: 8px 10px;
          border-radius: 10px;
        }

        @keyframes goldBlink {
          0% {
            opacity: .50;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.40)) drop-shadow(0 2px 10px rgba(0,0,0,.10));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.75)) drop-shadow(0 3px 18px rgba(183,155,74,.38));
          }
          100% {
            opacity: .50;
            filter: drop-shadow(0 1px 2px rgba(255,255,255,.40)) drop-shadow(0 2px 10px rgba(0,0,0,.10));
          }
        }

        .hitBlock::after {
          content: "";
          position: absolute;
          inset: -2px;
          border-radius: 12px;

          border: 3px solid rgba(183,155,74,.98);
          box-shadow:
            0 0 0 1px rgba(255,255,255,.55) inset,
            0 2px 10px rgba(0,0,0,.10);

          animation: goldBlink 2.4s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .hitLine1, .hitLine2 {
          position: relative;
          z-index: 1;

          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;

          font-weight: 900;
          font-size: clamp(14px, 4.6vw, 18px); /* ★LPと同一 */
          line-height: 1.12;
          letter-spacing: .01em;
          color: #1b2a33;

          text-shadow:
            0 1px 2px rgba(255,255,255,.85),
            0 2px 12px rgba(0,0,0,.10);

          white-space: nowrap;
        }

        .hitBlock.d1::after { animation-delay: 0s; }
        .hitBlock.d2::after { animation-delay: .18s; }
        .hitBlock.d3::after { animation-delay: .36s; }

        .hitLeft {
          flex: 1 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: clip;
        }
        .hitRight {
          flex: 0 0 auto;
          text-align: right;
        }

        .hitTicketLeft { opacity: .98; }

        .hitAmount {
          font-weight: 900;
          font-size: clamp(16px, 5.2vw, 22px); /* ★LPと同一 */
          color: rgba(183,155,74,1);

          animation: goldBlink 2.4s ease-in-out infinite;
          will-change: opacity, filter;
        }
        .hitAmount.d1 { animation-delay: 0s; }
        .hitAmount.d2 { animation-delay: .18s; }
        .hitAmount.d3 { animation-delay: .36s; }

        .hitEmpty {
          font-weight: 900;
          font-size: clamp(14px, 4.6vw, 18px);
          color: rgba(27,42,51,.88);
          text-shadow: 0 1px 2px rgba(255,255,255,.85);
        }

        @media (prefers-reduced-motion: reduce) {
          .lineButtonLuxury { animation: none; }
          .koukaiRecoText { animation: none; }
          .koukaiRecoText::after { animation: none; }
          .hitBlock::after { animation: none; }
          .hitAmount { animation: none; }
        }
      `}</style>

      <div
        className="stack"
        style={{
          maxWidth: 720,
          width: "100%",
          margin: "0 auto",
          padding: 0,
          background: "#fff",
        }}
      >
        {SECTIONS.map((s, i) => {
          if (s.kind === "image") {
            const isKoukai = s.id === "lp-top2";
            const isJisseki = s.id === "lp-top4";

            return (
              <div
                key={`img-${i}`}
                id={s.id}
                style={{
                  margin: 0,
                  padding: 0,
                  lineHeight: 0,
                  position: "relative",
                }}
              >
                <img
                  src={s.src}
                  alt={s.id || ""}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    margin: 0,
                    padding: 0,
                  }}
                />

                {/* lp-top2：本日の公開情報（リンク無し） */}
                {isKoukai && recoLines.length > 0 && (
                  <div className="koukaiRecoInImage" aria-label="おすすめプラン">
                    {recoLines.map((t, idx) => {
                      const cls = idx === 0 ? "d1" : idx === 1 ? "d2" : "d3";
                      return (
                        <div
                          key={idx}
                          className={`koukaiRecoText ${cls}`}
                          title={t}
                        >
                          {t}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* lp-top4：直近の的中実績（リンク無し） */}
                {isJisseki && (
                  <div className="hitsInCard" aria-label="直近の実績（3件）">
                    {resultRows.length === 0 ? (
                      <div className="hitEmpty">続きはまだありません。</div>
                    ) : (
                      resultRows.slice(0, 3).map((r, idx) => {
                        const cls = idx === 0 ? "d1" : idx === 1 ? "d2" : "d3";
                        const payout = r.payout != null ? fmtMoney(r.payout) : "";
                        const status = r.status || "的中";

                        // ★買い目が空の時でも「item」から拾えなかったケースなので最後の保険
                        const ticketCombo =
                          `${r.ticket || ""}${r.ticket && r.combo ? "  " : ""}${r.combo || ""}`.trim();

                        return (
                          <div className={`hitBlock ${cls}`} key={`${idx}-${r.date}-${r.plan}`}>
                            <div className="hitLine1">
                              <div className="hitLeft">{r.date || ""}</div>
                              <div className="hitRight">{r.plan || ""}</div>
                            </div>

                            <div className="hitLine2">
                              <div className="hitLeft hitTicketLeft">{ticketCombo || ""}</div>
                              <div className={`hitRight hitAmount ${cls}`}>
                                {payout ? `${payout} 円 ${status}` : `${status}`}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          }

          // banner（line-tomodachi.webp）
          return (
            <div
              key={`ban-${i}`}
              id={s.id}
              style={{
                padding: 0,
                lineHeight: 0,
                marginTop: BANNER_PULL,
                marginBottom: BANNER_PULL,
              }}
            >
              <a
                href={LINE_URL}
                onClick={handleLogin}
                style={{
                  display: "block",
                  margin: 0,
                  padding: 0,
                  textDecoration: "none",
                }}
                aria-label="LINEログインへ"
              >
                <img
                  src={`${base}line-tomodachi.webp`}
                  alt="LINEログイン"
                  loading="lazy"
                  className="lineButtonLuxury"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    margin: 0,
                    padding: 0,
                  }}
                />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}